import useSWR from 'swr';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import {
  DsNum,
  DsButton,
  DsPageHeader,
  DsEmptyState,
  DsStatusChip,
} from 'src/auditoria/components/ds';

import {
  type ImovelList,
  type ImovelDetalhe,
  formatArea,
  formatInscricao,
  getTipoImovelLabel,
} from './types';

// ----------------------------------------------------------------------
// Meu Imóvel → vínculos do contribuinte.
//
// Canvas (`portal-meu-imovel.dc.html` e `portal-meu-imovel-detalhe-imovel.dc.html`):
// é UMA tela em duas colunas — lista de cards (flex:1) à esquerda e coluna fixa de
// 330px com o detalhe do imóvel selecionado à direita. O segundo artboard é o mesmo
// desenho com o card selecionado contornado em `primary`. Por isso o detalhe deixou
// de ser só uma rota separada e passou a viver aqui; a página de cadastro completo
// (abas de proprietários, características, valores e avaliações) continua em
// `/meu-imovel/:id`, alcançável pelo botão no pé do painel.
// ----------------------------------------------------------------------

const STATUS_ATIVO = 1;
const STATUS_BLOQUEADO = 3;

function situacaoImovel(status: number): {
  rotulo: string;
  tom: 'success' | 'error' | 'default';
} {
  if (status === STATUS_ATIVO) return { rotulo: 'Ativo', tom: 'success' };
  if (status === STATUS_BLOQUEADO) return { rotulo: 'Bloqueado', tom: 'error' };
  return { rotulo: 'Desativado', tom: 'default' };
}

/** `.kv` do canvas: rótulo à esquerda, valor tabular à direita, régua tracejada. */
function LinhaKv({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '10px',
        padding: '7px 0',
        fontSize: 12,
        borderBottom: '1px dashed',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {rotulo}
      </Box>
      <DsNum sx={{ fontWeight: 700, textAlign: 'right' }}>{valor ?? '—'}</DsNum>
    </Box>
  );
}

function ImovelCard({
  imovel,
  selecionado,
  onClick,
}: {
  imovel: ImovelList;
  selecionado: boolean;
  onClick: () => void;
}) {
  const endereco =
    [[imovel.logradouro, imovel.numero_porta].filter(Boolean).join(', '), imovel.bairro]
      .filter(Boolean)
      .join(' · ') || '—';

  // O artboard escreve "Lote 12 · Quadra 08 · 312 m²", mas quadra e lote só vêm
  // no detalhe (`ImovelDetalhe`); a lista entrega tipo e áreas. Mailbox.
  const linhaCadastro = [
    getTipoImovelLabel(imovel.tipo_enum),
    imovel.area_lote ? `${formatArea(imovel.area_lote)} m² de lote` : null,
    imovel.area_construida_unidade
      ? `${formatArea(imovel.area_construida_unidade)} m² construídos`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const { rotulo, tom } = situacaoImovel(imovel.status_enum);

  return (
    <Card
      aria-current={selecionado || undefined}
      sx={{
        mb: '10px',
        border: '1px solid',
        borderColor: selecionado ? 'primary.main' : 'transparent',
      }}
    >
      <CardActionArea onClick={onClick} sx={{ padding: '16px 18px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 13, fontWeight: 800 }}>
              Inscrição <DsNum>{formatInscricao(imovel.inscricao) || '—'}</DsNum>
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{endereco}</Box>
            <Box sx={{ fontSize: 11, color: 'text.secondary' }}>{linhaCadastro}</Box>
          </Box>

          <DsStatusChip tone={tom}>{rotulo}</DsStatusChip>
        </Box>
      </CardActionArea>
    </Card>
  );
}

/** Coluna fixa de 330px: o detalhe do imóvel selecionado, em `.kv`. */
function PainelDetalhe({ id, onAbrirCadastro }: { id: number; onAbrirCadastro: () => void }) {
  const { data } = useSWR<ImovelDetalhe>(`/portal-api/meu-imovel/${id}`, fetcher);

  const inscricao = data ? formatInscricao(data.inscricao) || '—' : '…';

  return (
    <Card
      sx={{
        flex: { xs: '1 1 auto', md: '0 0 330px' },
        width: '100%',
        padding: '18px 20px',
      }}
    >
      <Box component="h2" sx={{ fontSize: 13, fontWeight: 800, m: '0 0 6px' }}>
        Detalhe do Imóvel · {inscricao}
      </Box>

      {data ? (
        <>
          <LinhaKv rotulo="Inscrição" valor={formatInscricao(data.inscricao) || '—'} />
          <LinhaKv
            rotulo="Inscrição anterior"
            valor={formatInscricao(data.inscricao_anterior) || '—'}
          />
          <LinhaKv rotulo="Responsável" valor={data.responsavel || '—'} />
          <LinhaKv rotulo="Tipo" valor={getTipoImovelLabel(data.tipo_enum)} />
          <LinhaKv rotulo="Quadra / Lote" valor={`${data.quadra || '—'} / ${data.lote || '—'}`} />
          <LinhaKv rotulo="Área do lote (m²)" valor={formatArea(data.area_lote)} />
          <LinhaKv rotulo="Área construída (m²)" valor={formatArea(data.area_construida_unidade)} />
          <LinhaKv rotulo="Isento IPTU" valor={data.imovisento_iptu ? 'Sim' : 'Não'} />
          <LinhaKv rotulo="Isento TSU" valor={data.imovisento_tsu ? 'Sim' : 'Não'} />

          {/*
            Acréscimo ao artboard: sem este botão a página de cadastro completo
            (proprietários, características, valores declarados e avaliações)
            ficaria órfã. Registrado no mailbox.
          */}
          <Box sx={{ mt: '12px' }}>
            <DsButton
              tone="neutral"
              size="tall"
              onClick={onAbrirCadastro}
              sx={{ width: '100%', justifyContent: 'center' }}
            >
              Ver cadastro completo
            </DsButton>
          </Box>
        </>
      ) : (
        <Box sx={{ fontSize: 12, color: 'text.secondary', padding: '7px 0' }}>
          Carregando o detalhe do imóvel…
        </Box>
      )}
    </Card>
  );
}

export default function MeuImovelListPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useSWR<ImovelList[]>('/portal-api/meu-imovel', fetcher);
  const [selecionadoId, setSelecionadoId] = useState<number | null>(null);

  const imoveis = data ?? [];
  const selecionado = imoveis.find((i) => i.id === selecionadoId) ?? imoveis[0];
  const vazio = !isLoading && imoveis.length === 0;

  return (
    <DashboardContent>
      <DsPageHeader
        weight={800}
        title="Meu Imóvel"
        subtitle="Imóveis vinculados ao cadastro · inscrição, áreas e isenções."
        action={
          <DsStatusChip>
            <DsNum>{imoveis.length}</DsNum>&nbsp;vínculo(s)
          </DsStatusChip>
        }
      />

      {vazio ? (
        <Card>
          <DsEmptyState
            icon="info"
            title="Nenhum imóvel vinculado"
            description="Não há imóveis vinculados ao seu cadastro no município. Se você é proprietário e não vê seu imóvel aqui, procure a prefeitura para atualizar o cadastro."
          />
        </Card>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {imoveis.map((imovel) => (
              <ImovelCard
                key={imovel.id}
                imovel={imovel}
                selecionado={imovel.id === selecionado?.id}
                onClick={() => setSelecionadoId(imovel.id)}
              />
            ))}
          </Box>

          {selecionado && (
            <PainelDetalhe
              id={selecionado.id}
              onAbrirCadastro={() => navigate(`/meu-imovel/${selecionado.id}`)}
            />
          )}
        </Box>
      )}
    </DashboardContent>
  );
}
